import { useState, useEffect, useContext } from 'react'
import { path } from 'ramda'
import { Typography, Link, Notification, Box } from '@telus-uds/components-web'
import { func, object } from 'prop-types'
import { utils } from '@digital-commerce/shared-utils'
import localeContext from '../../../../contexts/Locale'
import useAbTesting from '../../../../hooks/useAbTesting'
import { isSHSCart } from '../../../../utils/cart'
import { NoWrap } from './styles'
import { parsePersonalizationApiError } from '../util'
import { getLWCService } from '../../../organisms/LivingWellCompanion/utils'
import { getMobilityOffers } from '../../../../utils/mobility'

const {
  cart: { isCartMobilityAndHs, isBusinessCart, getHSService }
} = utils

const isMHorBusinessCart = (cart) => isCartMobilityAndHs(cart) || isBusinessCart(cart)

const PARENT_CONTAINER_ID_HS_CALL_AGENT_BANNER = '2vYsnPx0HMBEjzsFdWVjMO'
const checkLWCRenderingRequirement = (cart) => {
  const lwcServices = getLWCService(cart)
  const mobilityServices = getMobilityOffers(cart)
  return !!lwcServices && mobilityServices.length === 0
}
const checkInternetRenderingRequirement = (cart, featureFlags) => {
  const showHSBanner = featureFlags.hSCallAgentBanner
  const isHSProvince = ['AB', 'BC'].includes(cart.root.province?.toUpperCase())
  const hasInternetService = !!getHSService(['internet'], cart)
  return hasInternetService && showHSBanner && isHSProvince && !isMHorBusinessCart(cart)
}
// Separate requirement check for SHS so this isn't dependent on AB test logic for internet
const checkSHSRenderingRequirement = (cart, featureFlags) => {
  return isSHSCart(cart) && !isMHorBusinessCart(cart) && featureFlags?.shsCallAgentBanner
}
const CallAgentNotification = ({ translate, cart, featureFlags, setHasVisitorStateCookie }) => {
  const { prov } = useContext(localeContext)
  const [shouldDisplay, setShouldDisplay] = useState(false)
  const startABTesting = useAbTesting()
  const isBrowserProvinceQC = prov?.toUpperCase() === 'QC'

  useEffect(() => {
    const getDisplayResult = async () => {
      try {
        const meetsInternetRenderingRequirement = checkInternetRenderingRequirement(
          cart,
          featureFlags
        )
        const meetsSHSRenderingRequirement = checkSHSRenderingRequirement(cart, featureFlags)
        const meetsLWCRenderingRequirement = checkLWCRenderingRequirement(cart)
        const abTestingResult = await startABTesting(
          PARENT_CONTAINER_ID_HS_CALL_AGENT_BANNER,
          path(['activityContainers', 0, 'fields', 'toggle'])
        )
        if (meetsLWCRenderingRequirement) {
          setShouldDisplay(true)
        }
        // Banner should not be displayed in QC due to legal language requirements
        else if (isBrowserProvinceQC) {
          setShouldDisplay(false)
        } else if (meetsSHSRenderingRequirement) {
          setShouldDisplay(true)
        } else if (meetsInternetRenderingRequirement && abTestingResult === true) {
          setShouldDisplay(true)
        }
        setHasVisitorStateCookie({ status: true, error: undefined })
      } catch (err) {
        setHasVisitorStateCookie({ status: true, error: parsePersonalizationApiError(err) })
      }
    }

    getDisplayResult()
  }, [])

  const getBannerNumber = () => {
    if (checkInternetRenderingRequirement(cart, featureFlags)) {
      return 'bannerNumberHS'
    }
    if (checkSHSRenderingRequirement(cart, featureFlags)) {
      return 'bannerNumberSHS'
    }
    return 'bannerNumberLWC'
  }

  const renderNumberLink = () => {
    const bannerNumber = getBannerNumber()
    const bannerNumberTransPath = `appNotification.callAgentNotification.${bannerNumber}`
    return (
      <Link
        href={`tel:${translate(bannerNumberTransPath)}`}
        testID="agent-banner-link"
        variant={{ style: 'inline', size: 'small' }}
      >
        <NoWrap>{translate(bannerNumberTransPath)}</NoWrap>
      </Link>
    )
  }
  if (!shouldDisplay) {
    return null
  }
  return (
    <Box top={{ xs: 4, lg: 5 }}>
      <Notification tokens={{ icon: '' }} dismissible testID="call-agent-notification">
        <Typography variant={{ size: 'medium' }}>
          {translate('appNotification.callAgentNotification.bannerQuestion')}
          {renderNumberLink()}
        </Typography>
      </Notification>
    </Box>
  )
}
CallAgentNotification.propTypes = {
  translate: func.isRequired,
  cart: object.isRequired,
  featureFlags: object.isRequired,
  setHasVisitorStateCookie: func.isRequired
}

export default CallAgentNotification
